import { createTheme, colors, Slide, Fade } from "@mui/material"
import defaultTheme from "./default"
import { ExpandMore } from '@mui/icons-material'
import { forwardRef } from 'react'

const SlideUpTransition = forwardRef( function SlideUpTransition ( props, ref ) {
    return <Slide direction="up" ref={ref} {...props} />
} )


function getFullscreenElement () {
    if ( typeof document === "undefined" ) return null
    // Standard + vendor-prefixed fallbacks
    return (
        document.fullscreenElement ??
        ( document ).webkitFullscreenElement ??
        ( document ).mozFullScreenElement ??
        ( document ).msFullscreenElement ??
        null
    )
}

const lightTheme = createTheme( {
    theme: 'light',
    border: [
        "none",
        '1px solid #E4E4E7',
    ],
    shadows: [
        "none",
        "0px 0px 4px 1px rgba(0,0,0,0.1)",
        ...defaultTheme.shadows.slice( 2 )
    ],
    table: {
        headerBg: "#f1f1f1",
        headerColor: '#000000',
        color: '#09090b',
        fontSize: "14px"
    },
    zIndex: {
        modal: 3000,
        tooltip: 5000,
    },
    layout: {
        filterElementHeight: "37.4px",
        pagePadding: { md: "20px", xs: "10px" },
        borderRadius: 5,
        cardsGap: 1,
        sectionsGap: 2,
    },
    palette: {
        mode: 'light',
        borderColor: "#D7D7D7",
        contentBg: "#f4f4f4",
        defaultColor: "#000",
        shadedText: "#373737",
        greyedOut: "#E5E4E2",
        layoutBackground: "#EFEFEF" || "#0F172A" || `linear-gradient( 90deg, rgb(3, 0, 52) 25.54%, rgb(10, 0, 154) 205.66% )`,
        cardBackground: "#fff",
        contrastText: "#000",

        form: {
            labelColor: '#09090b',
            formCardBg: "#ffffff",
            formBg: "#FCFCFC",
            placeholder: "#b3b3b3",
            inputBg: "#fff"
        },
        nav: {
            font: "#000000" || "#ffffff",
            activeFont: "#ffffff" || "#ffffff",
            sidebar: "#EFEFEF" || "#0F172A" || "linear-gradient(90deg, #030034 25.54%, #0A009A 205.66%)",
            sidebarHover: "#05005c11",
            activeSidebarItem: "#24317A" || "#db8d06",
            sidebarItemHoverBg: "#05005c11",
            selected: "#151D48"
        },

        action: {
            hoverOpacity: 0.2,
            hover: "#eaeaea",
        },

        primary: {
            main: '#24317A',
            light: '#4251a8',
            dark: '#1d296a',
            contrastText: '#f8fafc'
        },
        primaryDark: {
            main: '#05004E',
            light: '#3c4994',
            dark: '#101636',
            contrastText: '#fff'
        },
        errorMessage: {
            main: "#F44336",
            light: "#FF8D9D",
            dark: "#de5f71",
            contrastText: "#fff"
        },
        customThemeColor: {
            main: "#E25A26",
            light: "#d17959",
            dark: "#d94c16",
            contrastText: "#fff"
        },
        greyed: {
            main: "#18181b",
            light: "#27272a",
            dark: "#09090b",
            contrastText: "#ffffff"
        },
        contrast: {
            main: colors.grey[800],
            light: colors.grey[600],
            dark: colors.grey[900],
            contrastText: "#fff"
        },
        text: {
            secondary: "#666666",
            primary: "#212121",
        },
        rowHover: "#f7f7f7"
    },
    typography: {
        fontFamily: "'Poppins', sans-serif",
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 600,
        h6: { color: "#1F1F1F" },
        h5: { color: "#1F1F1F" },
        h4: { color: "#1F1F1F" },
        h3: { color: "#1F1F1F" },
        h2: { color: "#1F1F1F" },
        h1: { color: "#1F1F1F", fontSize: "1rem", fontWeight: 600 },
        subtitle2: { color: "#444746", fontSize: "0.875rem", fontWeight: 500 },
        subtitle1: { color: "#1F1F1F", fontSize: "0.875rem", fontWeight: 400 },
        body1: { color: "#1F1F1F", fontSize: "0.875rem" },
        body2: { color: "#1F1F1F", fontSize: "0.75rem" },
    },
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    h1: 'h1',
                    h2: 'h1',
                    h3: 'h1',
                    h4: 'h1',
                    h5: 'h1',
                    h6: 'h1',
                    subtitle1: 'h6',
                    subtitle2: 'span',
                    body1: 'p',
                    body2: 'p',
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: "10px"
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: "20px",
                    border: "1px solid #E4E4E7",
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    border: "none",
                    fontSize: "14px"
                }
            }
        },
        MuiInputBase: {
            defaultProps: {
                color: "secondary",
            },
            styleOverrides: {
                root: {
                    background: "#fff",
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976d2", // optional
                        borderWidth: "1px", // keep original border
                    },
                    "&.Mui-focused": {
                        boxShadow: "0 0 0 1px rgba(116, 116, 116, 0.4)", // glow effect outside
                    },
                },
                input: {
                    fontSize: "14px",
                    fontWeight: "300"
                }
            },
        },
        MuiMenu: {
            defaultProps: {
                autoFocus: true
            },
            styleOverrides: {
                paper: {
                    padding: "0",
                    borderRadius: "20px",
                    overflow: "auto",
                    maxHeight: "350px",
                    marginTop: "5px"
                }
            },
        },
        MuiDialog: {
            defaultProps: {
                transitionDuration: {
                    enter: 200,
                    exit: 200
                },
                slots: {
                    transition: SlideUpTransition,
                },
                slotProps: {
                    backdrop: {
                        slots: {
                            transition: Fade,
                        },
                        transitionDuration: {
                            enter: 200,
                            exit: 0
                        }
                    },
                    paper: {
                        className: "custom-scrollbar",
                        sx: {
                            borderRadius: "20px",
                            width: "90%"
                        }
                    }
                }
            },
            styleOverrides: {
                root: ( { theme, ownerState } ) => ( {
                    '& .MuiDialog-container': {
                        [theme.breakpoints.down( 'sm' )]: { alignItems: "flex-end" },
                    },
                    '& .MuiDialog-paper': {
                        margin: 0,
                        maxWidth: ownerState.maxWidth || 1024,
                        width: "90%",
                        [theme.breakpoints.down( 'sm' )]: { width: "100%", maxHeight: '90%' },
                        borderRadius: { xs: `${theme.layout.borderRadius * 4}px ${theme.layout.borderRadius * 4}px 0 0`, sm: theme.layout.borderRadius },
                    },
                } )
            },
            variants: [
                {
                    props: { variant: 'dock-to-bottom' },
                    style: ( { theme, ownerState } ) => ( {
                        '& .MuiDialog-container': {
                            [theme.breakpoints.down( 'sm' )]: { alignItems: "flex-end" },
                        },
                        '& .MuiDialog-paper': {
                            margin: 0,
                            maxWidth: ownerState.maxWidth || 1024,
                            width: "90%",
                            [theme.breakpoints.down( 'sm' )]: { width: "100%", maxHeight: '90%' },
                            borderRadius: { xs: `${theme.layout.borderRadius * 4}px ${theme.layout.borderRadius * 4}px 0 0`, sm: theme.layout.borderRadius },
                        },
                    } )
                }
            ]
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: 0,
                }
            }
        },
        MuiTooltip: {
            defaultProps: {
                disableInteractive: true,
                slotProps: {
                    popper: {
                        container: ( anchorEl ) => {
                            const fs = getFullscreenElement()
                            // Prefer an HTMLElement; otherwise fall back to body
                            return ( fs instanceof HTMLElement ? fs : document.body )
                        }
                    }
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    "&.Mui-focused": {
                        boxShadow: "none", // glow effect outside
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: defaultTheme.palette.divider, // Much darker on focus
                        borderWidth: '1px',   // Optional: make it slightly thicker
                    },
                }
            },
            defaultProps: {
                IconComponent: ExpandMore,
                MenuProps: {
                    anchorOrigin: {
                        vertical: "bottom", horizontal: "left"
                    },
                    transformOrigin: {
                        vertical: "top", horizontal: "left"
                    },
                    slotProps: {
                        paper: {
                            sx: { marginTop: "5px", border: `1px solid #E4E4E7` }
                        }
                    }
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    margin: 0,
                    marginTop: "3px",
                    color: "#F44336",
                }
            }
        },

        MuiButton: {
            defaultProps: {
                loadingPosition: "start",
                disableElevation: true,
                size: "small"
            },
            styleOverrides: {
                root: {
                    textTransform: "capitalize",
                    borderRadius: 8,
                },
                sizeSmall: { fontSize: "0.75rem", minHeight: 32 },
                sizeMedium: { minHeight: 40 },
                sizeLarge: { minHeight: 48 },
            },
            variants: [
                {
                    props: { variant: 'muted', size: "small" },
                    style: ( { theme, ownerState } ) => {

                        const buttonColorProp = ownerState.color && ownerState.color !== 'inherit'
                            ? ownerState.color
                            : 'primary'

                        const targetColorPalette = theme.palette[buttonColorProp]

                        return {
                            backgroundColor: 'transparent',
                            textTransform: 'none',
                            fontSize: "0.75rem",
                            padding: "5px 10px",

                            // 3. Set the base text color to the palette's main color
                            color: theme.palette.text.secondary,

                            transition: theme.transitions.create( ['background-color', 'color'], {
                                duration: theme.transitions.duration.short,
                            } ),

                            '&:hover': {
                                backgroundColor: theme.alpha( targetColorPalette?.main || '0, 0, 0', 0.2 ),
                                color: targetColorPalette?.main || theme.palette.text.primary,
                            },
                        }
                    }
                },
                {
                    props: { variant: 'muted' },
                    style: ( { theme, ownerState } ) => {

                        const buttonColorProp = ownerState.color && ownerState.color !== 'inherit'
                            ? ownerState.color
                            : 'primary'

                        const targetColorPalette = theme.palette[buttonColorProp]

                        return {
                            backgroundColor: 'transparent',
                            textTransform: 'none',
                            fontSize: "0.75rem",

                            // 3. Set the base text color to the palette's main color
                            color: theme.palette.text.secondary,

                            transition: theme.transitions.create( ['background-color', 'color'], {
                                duration: theme.transitions.duration.short,
                            } ),

                            '&:hover': {
                                backgroundColor: theme.alpha( targetColorPalette?.main || '0, 0, 0', 0.1 ),
                                color: targetColorPalette?.main || theme.palette.text.primary,
                            },
                        }
                    }
                }
            ]
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    margin: "5px",
                    minHeight: 0,
                    borderRadius: "20px",
                    padding: "5px 10px",
                    ":hover": {
                        background: "#fef3e766",
                    },
                    '&.Mui-selected:hover': {
                        background: "#fef3e7",
                        color: "#d4770c"
                    },
                    '&.Mui-selected': {
                        background: "#fef3e7",
                        color: "#d4770c"
                    },
                }
            }
        },

        MuiSkeleton: {
            defaultProps: {
                animation: "wave"
            },
            styleOverrides: {
                root: {
                    transform: "scale(1)",
                    borderRadius: 10,
                }
            }
        },

        // CUSTOM COMPOENENTS FRO DATE AND TIME PICKERS

        MuiPickerOutlinedInput: {
            defaultProps: {
                slotProps: {
                    textField: {
                        size: "small",
                        slotProps: {
                            input: {
                                sx: {
                                    bgcolor: "#fff"
                                }
                            }
                        }
                    },
                    desktopPaper: {
                        sx: {
                            border: '1px solid #E4E4E7',
                            marginY: "5px",
                            bgcolor: "#fff",
                            boxShadow: `0px 1px 4px 1px rgba(0,0,0,0.1)`,
                        }
                    },
                    mobilePaper: {
                        sx: {
                            border: '1px solid #E4E4E7',
                            marginY: "5px",
                            bgcolor: "#fff",
                            boxShadow: `0px 1px 4px 1px rgba(0,0,0,0.1)`,
                        }
                    },
                    popper: {
                        sx: {
                            margin: "10px",
                            zIndex: defaultTheme.zIndex.tooltip * 4
                        }
                    }
                }
            }
        },
    }
} )

export default lightTheme